import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/perf";
import { zoomCard } from "@/components/CardZoom";
import HuntsSection from "@/components/Hunts";
import CardWarsManual from "@/components/CardWarsManual";
import CardWarsPacks from "@/components/CardWarsPacks";
import BullpenArena from "@/components/BullpenArena";
import Skybox from "@/components/Skybox";
import Callouts from "@/components/Callouts";
import { useAuth } from "@/context/AuthContext";
import { Swords, MapPin, Trophy, Loader2, Coins, Zap, X, Sparkles, Flame, Shield, BookOpen, HelpCircle, Crown } from "lucide-react";

const RCOLOR = {
  exclusive: "border-pb-red/70 text-pb-red", elite: "border-amber-400/70 text-amber-300",
  gold: "border-yellow-500/70 text-yellow-400", gem: "border-sky-400/60 text-sky-300",
  holo: "border-fuchsia-400/60 text-fuchsia-300", rare: "border-sky-500/60 text-sky-400",
  standard: "border-zinc-600 text-zinc-400", common: "border-stone-500/60 text-stone-400",
};
const STAT_KEYS = ["dink", "power", "speed", "iq", "clutch"];
const pwrTier = (p) => p >= 80 ? ["LEGEND", "text-fuchsia-300 border-fuchsia-400/60 bg-fuchsia-400/10"]
  : p >= 70 ? ["ELITE", "text-pb-red border-pb-red/60 bg-pb-red/10"]
  : p >= 60 ? ["STRONG", "text-amber-300 border-amber-400/60 bg-amber-400/10"]
  : p >= 50 ? ["CONTENDER", "text-yellow-300 border-yellow-500/50 bg-yellow-500/10"]
  : ["ROOKIE", "text-zinc-400 border-zinc-600 bg-zinc-800/40"];

export const sndVictory = () => {
  try {
    const c = _actx(), t = c.currentTime;
    [[523.25, 0], [659.25, 0.13], [783.99, 0.26], [1046.5, 0.4]].forEach(([f, at]) => {
      const o = c.createOscillator(), g = c.createGain();
      o.type = "triangle"; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t + at); g.gain.exponentialRampToValueAtTime(0.22, t + at + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + at + (at >= 0.4 ? 0.9 : 0.35));
      o.connect(g).connect(c.destination); o.start(t + at); o.stop(t + at + 1);
    });
    const sh = c.createOscillator(), sg = c.createGain();
    sh.type = "sine"; sh.frequency.setValueAtTime(2093, t + 0.4); sh.frequency.linearRampToValueAtTime(2637, t + 0.9);
    sg.gain.setValueAtTime(0.0001, t + 0.4); sg.gain.exponentialRampToValueAtTime(0.06, t + 0.5);
    sg.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
    sh.connect(sg).connect(c.destination); sh.start(t + 0.4); sh.stop(t + 1.25);
  } catch { /* audio unsupported */ }
};
export const sndDefeat = () => {
  try {
    const c = _actx(), t = c.currentTime;
    [[233, 0, 0.4], [207, 0.42, 0.4], [175, 0.84, 0.9]].forEach(([f, at, dur]) => {
      const o = c.createOscillator(), g = c.createGain();
      const lp = c.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 900;
      o.type = "sawtooth"; o.frequency.setValueAtTime(f, t + at);
      o.frequency.exponentialRampToValueAtTime(f * 0.86, t + at + dur);
      g.gain.setValueAtTime(0.0001, t + at); g.gain.exponentialRampToValueAtTime(0.18, t + at + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, t + at + dur);
      o.connect(lp).connect(g).connect(c.destination); o.start(t + at); o.stop(t + at + dur + 0.05);
    });
  } catch { /* audio unsupported */ }
};

const CW_CSS = `
@keyframes cwSheen{0%{left:-60%}55%{left:130%}100%{left:130%}}
@keyframes cwPulseGold{0%,100%{filter:drop-shadow(0 0 10px rgba(250,204,21,.35)) drop-shadow(0 8px 16px rgba(0,0,0,.6))}50%{filter:drop-shadow(0 0 28px rgba(190,242,100,.75)) drop-shadow(0 8px 16px rgba(0,0,0,.6))}}
@keyframes cwPulseCyan{0%,100%{filter:drop-shadow(0 0 8px rgba(34,211,238,.3)) drop-shadow(0 8px 16px rgba(0,0,0,.6))}50%{filter:drop-shadow(0 0 24px rgba(34,211,238,.7)) drop-shadow(0 8px 16px rgba(0,0,0,.6))}}
.cw-btn-battle,.cw-btn-defense{position:relative;border:0;padding:0;background-color:transparent;background-position:center;background-repeat:no-repeat;background-size:100% 100%}
.cw-btn-battle{background-image:url('/icons/cw-btn-court.png');animation:cwPulseGold 2.6s ease-in-out infinite}
.cw-btn-defense{background-image:url('/icons/cw-btn-defense.png');animation:cwPulseCyan 2.6s ease-in-out infinite}
.cw-btn-battle:hover:not(:disabled){animation:none;filter:brightness(1.18) drop-shadow(0 0 34px rgba(190,242,100,.85)) drop-shadow(0 8px 16px rgba(0,0,0,.6))}
.cw-btn-defense:hover:not(:disabled){animation:none;filter:brightness(1.18) drop-shadow(0 0 28px rgba(34,211,238,.85)) drop-shadow(0 8px 16px rgba(0,0,0,.6))}
.cw-btn-battle:disabled,.cw-btn-defense:disabled{animation:none;filter:grayscale(.55) drop-shadow(0 8px 16px rgba(0,0,0,.6))}
.cw-btn-inner{position:relative;overflow:hidden;width:100%;height:100%;display:flex;align-items:center;justify-content:center}
.cw-btn-battle .cw-btn-inner{-webkit-mask:url('/icons/cw-btn-court.png') center/100% 100% no-repeat;mask:url('/icons/cw-btn-court.png') center/100% 100% no-repeat;color:#FFF7D6;text-shadow:0 0 8px rgba(250,204,21,.95),0 0 22px rgba(250,204,21,.55)}
.cw-btn-defense .cw-btn-inner{-webkit-mask:url('/icons/cw-btn-defense.png') center/100% 100% no-repeat;mask:url('/icons/cw-btn-defense.png') center/100% 100% no-repeat;color:#C7F9FF;text-shadow:0 0 8px rgba(34,211,238,.95),0 0 20px rgba(34,211,238,.55)}
.cw-btn-inner::after{content:"";position:absolute;top:0;bottom:0;left:-60%;width:38%;background:linear-gradient(105deg,transparent,rgba(255,255,255,.45),transparent);transform:skewX(-20deg);animation:cwSheen 3.2s ease-in-out infinite}
.cw-btn-battle:disabled .cw-btn-inner::after,.cw-btn-defense:disabled .cw-btn-inner::after{display:none}
@keyframes cwPulseTeal{0%,100%{filter:drop-shadow(0 0 8px rgba(45,212,191,.35)) drop-shadow(0 6px 12px rgba(0,0,0,.6))}50%{filter:drop-shadow(0 0 22px rgba(45,212,191,.7)) drop-shadow(0 6px 12px rgba(0,0,0,.6))}}
.cw-btn-skybox{position:relative;border:0;padding:0;background:url('/icons/cw-btn-skybox.png') center/100% 100% no-repeat;animation:cwPulseTeal 2.8s ease-in-out infinite}
.cw-btn-skybox:hover{animation:none;filter:brightness(1.2) drop-shadow(0 0 28px rgba(45,212,191,.85)) drop-shadow(0 6px 12px rgba(0,0,0,.6))}
.cw-btn-skybox .cw-btn-inner{-webkit-mask:url('/icons/cw-btn-skybox.png') center/100% 100% no-repeat;mask:url('/icons/cw-btn-skybox.png') center/100% 100% no-repeat;color:#CFFCF5;text-shadow:0 0 8px rgba(45,212,191,.95),0 0 20px rgba(45,212,191,.55)}
@keyframes cwFmtPulse{0%,100%{filter:drop-shadow(0 0 6px var(--fmt-glow)) drop-shadow(0 4px 10px rgba(0,0,0,.55))}50%{filter:drop-shadow(0 0 18px var(--fmt-glow)) drop-shadow(0 4px 10px rgba(0,0,0,.55))}}
.cw-fmt-plate{position:relative;border:0;padding:0;background:transparent;height:58px;transition:transform .2s}
.cw-fmt-bg{position:absolute;inset:0;background-size:100% 100%;background-position:center;background-repeat:no-repeat;transition:filter .25s;pointer-events:none}
.cw-fmt-plate.sel{animation:cwFmtPulse 2.2s ease-in-out infinite;transform:scale(1.03)}
.cw-fmt-plate:not(.sel) .cw-fmt-bg{filter:grayscale(.45) brightness(.65) drop-shadow(0 4px 10px rgba(0,0,0,.55))}
.cw-fmt-plate:not(.sel):hover .cw-fmt-bg{filter:grayscale(.1) brightness(.9) drop-shadow(0 0 12px var(--fmt-glow))}
.cw-fmt-inner{position:relative;width:100%;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px}
.cw-dbl-chip{position:relative;clip-path:polygon(8px 0,100% 0,100% calc(100% - 8px),calc(100% - 8px) 100%,0 100%,0 8px);padding:1px;background:linear-gradient(115deg,rgba(192,132,252,.9),rgba(88,28,135,.5));transition:filter .2s}
.cw-dbl-chip>span{clip-path:polygon(7px 0,100% 0,100% calc(100% - 7px),calc(100% - 7px) 100%,0 100%,0 7px);display:flex;align-items:center;gap:5px;padding:7px 14px;background:linear-gradient(180deg,#150a24,#090312)}
.cw-dbl-chip.sel{filter:drop-shadow(0 0 12px rgba(192,132,252,.75))}
.cw-dbl-chip.sel>span{background:linear-gradient(180deg,#3b1a63,#1b0a33);text-shadow:0 0 8px rgba(216,180,254,.9)}
.cw-dbl-chip:not(.sel){filter:grayscale(.5) brightness(.6)}
.cw-dbl-chip:not(.sel):hover{filter:brightness(.9)}
.cw-slot{display:flex;flex-direction:column;align-items:center;gap:4px}
.cw-slot-card{position:relative;width:100%;aspect-ratio:3/4;display:flex;align-items:flex-end;justify-content:center;padding-bottom:12px}
.cw-card-img{width:94%;max-height:100%;object-fit:contain;animation:cwCardFloat 4.2s ease-in-out infinite;filter:drop-shadow(0 12px 12px rgba(0,0,0,.7)) drop-shadow(0 0 12px rgba(250,204,21,.3));transition:filter .25s ease}
.cw-slot:hover .cw-card-img{filter:drop-shadow(0 16px 16px rgba(0,0,0,.7)) drop-shadow(0 0 22px rgba(250,204,21,.65))}
.cw-card-img.clutch{filter:drop-shadow(0 12px 12px rgba(0,0,0,.7)) drop-shadow(0 0 14px rgba(239,68,68,.55))}
.cw-slot:hover .cw-card-img.clutch{filter:drop-shadow(0 16px 16px rgba(0,0,0,.7)) drop-shadow(0 0 26px rgba(239,68,68,.9))}
@keyframes cwCardFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
.cw-slot-pad{position:absolute;bottom:2px;width:86%;height:14px;border-radius:50%;background:radial-gradient(ellipse at center,rgba(148,163,184,.14),rgba(148,163,184,.04) 55%,transparent 75%);pointer-events:none}
.cw-slot-pad.gold{background:radial-gradient(ellipse at center,rgba(250,204,21,.45),rgba(250,204,21,.1) 55%,transparent 75%);animation:cwPadPulse 3s ease-in-out infinite}
.cw-slot-pad.clutch{background:radial-gradient(ellipse at center,rgba(239,68,68,.5),rgba(239,68,68,.12) 55%,transparent 75%);animation:cwPadPulse 2.2s ease-in-out infinite}
@keyframes cwPadPulse{0%,100%{opacity:.5;transform:scaleX(.92)}50%{opacity:1;transform:scaleX(1)}}
.cw-slot-empty{display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:#52525B;font-size:22px;font-weight:300;animation:cwPadPulse 3.2s ease-in-out infinite}
.cw-slot-lbl{font-family:'JetBrains Mono',monospace;font-size:8px;letter-spacing:.3em;text-transform:uppercase;color:#71717A;white-space:nowrap}
.cw-clutch-tag{color:#F87171;font-size:7px;font-weight:900;letter-spacing:.25em;text-transform:uppercase;text-shadow:0 0 8px rgba(239,68,68,.85);line-height:1}
.cw-snap{position:relative;display:flex;align-items:flex-end;justify-content:center;width:100%;height:100%;animation:cwSnap .5s cubic-bezier(.2,.9,.3,1.15) both}
@keyframes cwSnap{0%{opacity:0;transform:scale(1.28) translateY(-12px);filter:brightness(4) blur(8px) saturate(0)}55%{opacity:1;filter:brightness(1.9) blur(1px) saturate(1.35)}100%{opacity:1;transform:scale(1) translateY(0);filter:brightness(1) blur(0)}}
.cw-snap-flash{position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse at center,rgba(250,204,21,.4),transparent 62%);animation:cwSnapFlash .55s ease-out both}
@keyframes cwSnapFlash{0%{opacity:1}100%{opacity:0}}
`;

let _cwCtx;
const _actx = () => (_cwCtx = _cwCtx || new (window.AudioContext || window.webkitAudioContext)());
const sndSlotTick = () => {
  try {
    const c = _actx(), t = c.currentTime;
    const o = c.createOscillator(), g = c.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(1500, t);
    o.frequency.exponentialRampToValueAtTime(880, t + 0.07);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.18, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
    o.connect(g).connect(c.destination);
    o.start(t); o.stop(t + 0.1);
    const h = c.createOscillator(), hg = c.createGain();
    h.type = "triangle"; h.frequency.value = 2600;
    hg.gain.setValueAtTime(0.0001, t);
    hg.gain.exponentialRampToValueAtTime(0.08, t + 0.008);
    hg.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    h.connect(hg).connect(c.destination); h.start(t); h.stop(t + 0.06);
  } catch { /* audio unavailable */ }
};
export const sndBattleEntrance = () => {
  try {
    const c = _actx(), t = c.currentTime, dur = 2.4;
    const buf = c.createBuffer(1, c.sampleRate * dur, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const roar = c.createBufferSource(); roar.buffer = buf;
    const rf = c.createBiquadFilter(); rf.type = "bandpass"; rf.Q.value = 0.6;
    rf.frequency.setValueAtTime(500, t); rf.frequency.linearRampToValueAtTime(1400, t + dur * 0.5);
    rf.frequency.linearRampToValueAtTime(700, t + dur);
    const rg = c.createGain();
    rg.gain.setValueAtTime(0.0001, t); rg.gain.exponentialRampToValueAtTime(0.3, t + 0.5);
    rg.gain.setValueAtTime(0.3, t + dur * 0.6); rg.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    roar.connect(rf).connect(rg).connect(c.destination); roar.start(t); roar.stop(t + dur);
    const hum = c.createOscillator(), hum2 = c.createOscillator(), hg = c.createGain();
    hum.type = "sawtooth"; hum2.type = "sawtooth";
    hum.frequency.setValueAtTime(55, t); hum.frequency.linearRampToValueAtTime(110, t + dur * 0.8);
    hum2.frequency.setValueAtTime(55.8, t); hum2.frequency.linearRampToValueAtTime(111.5, t + dur * 0.8);
    const hf = c.createBiquadFilter(); hf.type = "lowpass"; hf.frequency.value = 320;
    hg.gain.setValueAtTime(0.0001, t); hg.gain.exponentialRampToValueAtTime(0.16, t + 0.35);
    hg.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    hum.connect(hf); hum2.connect(hf); hf.connect(hg).connect(c.destination);
    hum.start(t); hum2.start(t); hum.stop(t + dur); hum2.stop(t + dur);
    const zap = c.createOscillator(), zg = c.createGain();
    zap.type = "square"; zap.frequency.setValueAtTime(1800, t + 0.9); zap.frequency.exponentialRampToValueAtTime(300, t + 1.05);
    zg.gain.setValueAtTime(0.0001, t); zg.gain.setValueAtTime(0.07, t + 0.9); zg.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
    zap.connect(zg).connect(c.destination); zap.start(t + 0.9); zap.stop(t + 1.15);
  } catch { /* audio unsupported */ }
};

const DeckCard = ({ c, order, isClutch, onSetClutch, onClick, dimmed }) => (
  <button data-testid={`cw-deck-card-${c.id}`} onClick={onClick}
    className={`relative text-left border p-1.5 transition-all active:scale-95 ${dimmed ? "opacity-35 grayscale" : ""} ${c.black_label ? "bg-black border-yellow-300 shadow-[0_0_14px_rgba(253,224,71,.45)]" : "bg-pb-surface"} ${order ? (isClutch ? "border-pb-red shadow-[0_0_12px_rgba(239,68,68,.4)]" : "border-yellow-400 shadow-[0_0_12px_rgba(250,204,21,.35)]") : c.black_label ? "" : "border-pb-border hover:border-yellow-400/50"}`}>
    {order ? (
      isClutch ? (
        <span className="absolute -top-2 -left-2 z-10 h-6 px-1.5 flex items-center justify-center gap-0.5 bg-black border border-pb-red text-pb-red font-display font-black text-[9px] uppercase tracking-widest"
          data-testid={`cw-deck-card-${c.id}-clutch-badge`} title="Your CLUTCH CARD — plays Round 5 when the CLUTCH bonus hits.">⚡ CLUTCH</span>
      ) : (
        <span className="absolute -top-2 -left-2 z-10 w-6 h-6 flex items-center justify-center bg-yellow-400 text-black font-display font-black text-xs" data-testid={`cw-deck-card-${c.id}-order`}>{order}</span>
      )
    ) : null}
    {order && !isClutch && onSetClutch && (
      <span role="button" data-testid={`cw-deck-card-${c.id}-set-clutch`}
        title="Make this your CLUTCH CARD — it plays Round 5 when the CLUTCH bonus hits"
        onClick={(e) => { e.stopPropagation(); onSetClutch(); }}
        className="absolute bottom-8 right-1 z-10 bg-black/90 border border-pb-red/60 text-pb-red text-[7px] font-black uppercase tracking-widest px-1 py-0.5 hover:bg-pb-red hover:text-white transition-colors">⚡ Set Clutch</span>
    )}
    <div className="relative">
      <img src={c.url} alt={c.name} loading="lazy" className="w-full aspect-[3/4] object-contain bg-black" />
      <span data-testid={`cw-deck-card-${c.id}-zoom`} title="Expand — full battle sheet" role="button"
        onClick={(e) => { e.stopPropagation(); zoomCard(c.url, c.name, null, c); }}
        className="absolute top-1 right-1 bg-black/85 border border-yellow-400/70 text-yellow-400 font-display font-black text-sm px-1.5 leading-tight cursor-zoom-in hover:bg-yellow-400 hover:text-black transition-colors">{c.stats.overall}</span>
      {c.slabbed && <span className="absolute bottom-1 left-1 bg-black/85 border border-zinc-300/50 text-zinc-200 text-[7px] font-black uppercase tracking-widest px-1 py-0.5">Slab</span>}
    </div>
    <div className="flex items-center justify-between gap-1 mt-1">
      <span className="text-[9px] font-semibold truncate">{c.name}</span>
      <span className={`shrink-0 text-[6.5px] font-black uppercase tracking-widest px-1 py-0.5 border ${RCOLOR[c.rarity] || RCOLOR.standard}`}>{c.rarity}</span>
    </div>
    <div className="flex items-center gap-1 mt-1" data-testid={`cw-deck-card-${c.id}-protection`}>
      <span className={`text-[6.5px] font-black uppercase tracking-widest px-1 py-0.5 border ${c.black_label
        ? "border-yellow-300 text-yellow-300 bg-gradient-to-r from-yellow-500/20 to-black"
        : c.protection_bonus >= 6 ? "border-zinc-300/60 text-zinc-200"
        : c.protection_bonus > 0 ? "border-teal-400/60 text-teal-300"
        : "border-zinc-700 text-zinc-600"}`}>
        {c.black_label ? "★ BLACK LABEL" : c.protection_label}{c.protection_bonus > 0 ? ` +${c.protection_bonus}` : ""}
      </span>
    </div>
    {(c.gender || c.specialist) && (
      <div className="flex items-center gap-1 mt-1 flex-wrap" data-testid={`cw-deck-card-${c.id}-class`}>
        {c.gender && (
          <span data-testid={`cw-deck-card-${c.id}-gender`} title={c.gender === "male" ? "Male card — plays Men's & Mixed doubles" : "Female card — plays Women's & Mixed doubles"}
            className={`text-[6.5px] font-black uppercase tracking-widest px-1 py-0.5 border ${c.gender === "male" ? "border-sky-400/60 text-sky-300" : "border-pink-400/60 text-pink-300"}`}>
            {c.gender === "male" ? "♂ MALE" : "♀ FEMALE"}
          </span>
        )}
        {c.specialist && (
          <span data-testid={`cw-deck-card-${c.id}-specialist`} title={c.specialist === "singles" ? "Bullpen-classified SINGLES SPECIALIST" : "Bullpen-classified DOUBLES SPECIALIST"}
            className={`text-[6.5px] font-black uppercase tracking-widest px-1 py-0.5 border ${c.specialist === "singles" ? "border-cyan-400/70 text-cyan-300 bg-cyan-400/10" : "border-violet-400/70 text-violet-300 bg-violet-400/10"}`}>
            {c.specialist === "singles" ? "◆ SINGLES SPECIALIST" : "◆ DOUBLES SPECIALIST"}
          </span>
        )}
      </div>
    )}
    {c.ability && (
      <div className="flex items-center gap-1 mt-1" data-testid={`cw-deck-card-${c.id}-ability`} title={c.ability.desc}>
        <Zap className="w-2.5 h-2.5 text-fuchsia-400 shrink-0" />
        <span className="text-[7px] font-black uppercase tracking-widest text-fuchsia-300 truncate">{c.ability.type} — {c.ability.desc}</span>
      </div>
    )}
    <div className="grid grid-cols-5 gap-px mt-1">
      {STAT_KEYS.map((s) => (
        <div key={s} className="text-center bg-pb-elevated/70 py-0.5">
          <div className="text-[5.5px] uppercase tracking-wider text-zinc-500">{s.slice(0, 3)}</div>
          <div className={`text-[8px] font-mono font-bold ${s === "clutch" ? "text-yellow-400" : "text-zinc-200"}`}>{c.stats[s]}</div>
        </div>
      ))}
    </div>
  </button>
);

function BattleResult({ result, onClose }) {
  const [shown, setShown] = useState(0);
  const totalR = result.rounds.length;
  useEffect(() => {
    if (shown >= totalR + 1) return;
    const t = setTimeout(() => setShown((s) => s + 1), shown === 0 ? 500 : 1200);
    return () => clearTimeout(t);
  }, [shown, totalR]);
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  const done = shown >= totalR + 1;
  const won = result.placement === 1;
  useEffect(() => {
    if (done) (won ? sndVictory : sndDefeat)();
  }, [done, won]);
  return (
    <div className="fixed inset-0 z-[80] bg-black/95 overflow-y-auto overscroll-contain p-4" data-testid="cw-result">
      <div className="max-w-2xl mx-auto py-6">
        <div className="flex items-center justify-between mb-5">
          <div className="font-display font-black uppercase text-xl text-yellow-400 flex items-center gap-2">
            <Swords className="w-5 h-5" /> {result.replay ? "Battle Replay" : "Court Clash"}
            {result.format_label && (
              <span className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-400 border border-zinc-600 px-2 py-1" data-testid="cw-result-format">
                {result.format_label}{result.doubles_type ? ` · ${result.doubles_type === "mens" ? "Men's" : result.doubles_type === "womens" ? "Women's" : "Mixed"}` : ""}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {!done && <button data-testid="cw-skip-btn" onClick={() => setShown(totalR + 1)} className="text-[10px] font-black uppercase tracking-widest border border-pb-border px-3 py-1.5 text-zinc-400 hover:text-white">Skip</button>}
            <button data-testid="cw-result-close" onClick={onClose}><X className="w-5 h-5 text-zinc-500 hover:text-white" /></button>
          </div>
        </div>
        {result.rounds.slice(0, Math.min(shown, totalR)).map((rd) => (
          <div key={rd.round} className="border border-pb-border bg-pb-surface p-3 mb-3 animate-fade-up" data-testid={`cw-round-${rd.round}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-500">{rd.dreambreaker ? "Overtime" : `Round ${rd.round}`}</span>
              {rd.dreambreaker && (
                <span className="text-[10px] font-display font-black uppercase px-2 py-0.5 border border-pb-red text-pb-red bg-pb-red/10 animate-pulse" data-testid="cw-dreambreaker-chip">
                  ⚡ DREAMBREAKER
                </span>
              )}
              <span className={`text-[10px] font-display font-black uppercase px-2 py-0.5 border ${rd.clutch ? "border-yellow-400 text-yellow-400 bg-yellow-400/10" : "border-pb-cyan/50 text-pb-cyan"}`}>
                {rd.stat}{rd.clutch ? " + CLUTCH" : ""}
              </span>
              {rd.clutch && <Zap className="w-3.5 h-3.5 text-yellow-400" />}
            </div>
            <div className="space-y-1">
              {[...rd.entries].sort((a, b) => b.points - a.points).map((e, i) => (
                <div key={i} className={`flex items-center gap-2 px-2 py-1.5 ${e.is_me ? "border border-yellow-400/50 bg-yellow-400/5" : "bg-pb-elevated/50"}`}>
                  {e.card_url ? <img src={e.card_url} alt="" className="w-6 h-8 object-cover shrink-0" /> : <span className="w-6 h-8 bg-black shrink-0" />}
                  <span className={`flex-1 min-w-0 text-xs truncate ${e.is_me ? "text-yellow-300 font-bold" : "text-zinc-300"}`}>
                    {e.player}{e.is_me ? " (YOU)" : ""} <span className="text-zinc-600">·</span> <span className="text-zinc-500">{e.card_name}</span>
                    {e.ability && (
                      <span className="ml-1.5 text-[8px] font-black uppercase tracking-widest text-fuchsia-300 border border-fuchsia-400/50 px-1 py-0.5">
                        ⚡ {e.ability.type} {e.ability.effect}
                      </span>
                    )}
                  </span>
                  <span className="text-xs font-mono text-zinc-300 shrink-0">{e.value}{e.clutch_bonus ? <span className="text-yellow-400"> +{e.clutch_bonus}</span> : null}</span>
                  <span className={`shrink-0 w-8 text-center text-xs font-display font-black ${e.points === 4 ? "text-[#A3E635]" : "text-zinc-500"}`}>+{e.points}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        {!done && <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 text-yellow-400 animate-spin" /></div>}
        {done && (
          <div className="border border-yellow-400/60 bg-yellow-400/5 p-5 animate-fade-up" data-testid="cw-final">
            <div className="font-display font-black uppercase text-lg mb-3 flex items-center gap-2">
              <Trophy className={`w-5 h-5 ${won ? "text-yellow-400" : "text-zinc-500"}`} /> Final Standings
            </div>
            <div className="space-y-1.5 mb-4">
              {result.standings.map((s, i) => (
                <div key={i} className={`flex items-center gap-3 px-3 py-2 ${s.is_me ? "border border-yellow-400/60 bg-yellow-400/10" : "bg-pb-elevated/50"}`}>
                  <span className={`w-5 font-display font-black ${i === 0 ? "text-yellow-400" : "text-zinc-500"}`}>{i + 1}</span>
                  <span className={`flex-1 text-sm font-semibold ${s.is_me ? "text-yellow-300" : ""}`}>{s.name}{s.is_me ? " (YOU)" : ""}</span>
                  <span className="font-mono font-bold text-sm">{s.points} pts</span>
                </div>
              ))}
            </div>
            {won ? (
              <div className="text-center" data-testid="cw-payout">
                <div className="font-display font-black uppercase text-2xl text-yellow-400 animate-fade-up flex items-center justify-center gap-2">
                  <Sparkles className="w-5 h-5" /> Victory!
                </div>
                {result.payout > 0 ? (
                  <div className="text-sm text-[#A3E635] font-mono font-bold mt-1">+{result.payout} BLC pot added to your wallet</div>
                ) : result.replay ? (
                  <div className="text-xs text-zinc-400 mt-1">Your defense lineup held the court against {result.challenger_name}. 🛡️</div>
                ) : (
                  <div className="text-xs text-zinc-400 mt-1">Practice win — take that lineup ranked for the pot.</div>
                )}
              </div>
            ) : (
              <div className="text-center text-sm text-zinc-400" data-testid="cw-lost">
                You placed <b className="text-white">#{result.placement}</b>{result.mode === "ranked" ? " — the pot stays in the Bullpen." : "."} Retool your lineup and run it back.
              </div>
            )}
            <button data-testid="cw-battle-again" onClick={onClose}
              className="mt-4 w-full bg-yellow-400 text-black font-black uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-95 transition-all">
              Back to the Bullpen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function countdownStr(iso) {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "resolving now";
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function FridayNightPanel({ event, lineup, savedLineup, busy, onEnter, onArena }) {
  const [stake, setStake] = useState("");
  const [panelRef, setPanelRef] = useState(null);
  useEffect(() => {
    if (panelRef && window.location.hash === "#friday") {
      setTimeout(() => panelRef.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    }
  }, [panelRef]);
  if (!event) return null;
  const usingSaved = lineup.length !== 5 && savedLineup?.length === 5;
  const lineupToUse = lineup.length === 5 ? lineup : (usingSaved ? savedLineup : null);
  const min = Number(event.entry_blc) || 5;
  const amt = stake === "" ? min : Number(stake);
  return (
    <div ref={setPanelRef} className="relative border border-pb-red/50 bg-gradient-to-br from-red-950/40 via-pb-surface to-black p-5 mb-8 animate-fade-up overflow-hidden" data-testid="cw-event-panel">
      <div className="absolute -top-8 -right-8 w-40 h-40 bg-pb-red/10 blur-3xl pointer-events-none" />
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <span className="font-display font-black uppercase text-lg flex items-center gap-2 text-pb-red">
          <Flame className="w-5 h-5" /> Friday Night Bullpen Battle
        </span>
        <span className="text-[9px] font-black uppercase tracking-[0.25em] text-white bg-pb-red/90 px-2.5 py-1 animate-pulse">Weekly · Winner Takes All</span>
        <button data-testid="cw-arena-btn" onClick={onArena}
          className="flex items-center gap-1.5 border border-yellow-400/60 text-yellow-400 text-[9px] font-black uppercase tracking-widest px-3 py-1.5 hover:bg-yellow-400 hover:text-black transition-colors">
          🏟 Enter the Arena
        </button>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-zinc-400" data-testid="cw-event-countdown">
          Resolves in <b className="text-white">{countdownStr(event.resolves_at)}</b>
        </span>
      </div>
      <p className="text-xs text-zinc-400 mb-4">
        Register any time this week — throw in <b className="text-white">as much BLC as you want</b> (min {min}) and lock your lineup. Friday night the highest-scoring lineup takes <b className="text-yellow-400">the entire pot</b>.
      </p>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="border border-yellow-400/50 bg-black/60 px-4 py-2.5" data-testid="cw-event-pot">
          <div className="text-[8px] font-black uppercase tracking-[0.3em] text-zinc-500">Live Pot</div>
          <div className="font-display font-black text-2xl text-yellow-400">{event.pot} <span className="text-sm">BLC</span></div>
        </div>
        <div className="border border-pb-border bg-black/60 px-4 py-2.5" data-testid="cw-event-entrants">
          <div className="text-[8px] font-black uppercase tracking-[0.3em] text-zinc-500">In The Pool</div>
          <div className="font-display font-black text-2xl text-white">{event.entrants} <span className="text-sm text-zinc-500">player{event.entrants === 1 ? "" : "s"}</span></div>
        </div>
        {event.entrant_names?.length > 0 && (
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 max-w-xs truncate">
            latest: {event.entrant_names.slice().reverse().join(" · ")}
          </div>
        )}
      </div>
      {event.entered ? (
        <div className="flex items-center gap-2 border border-[#A3E635]/60 bg-[#A3E635]/10 px-4 py-3 text-[#A3E635] text-xs font-black uppercase tracking-widest" data-testid="cw-event-entered">
          <Shield className="w-4 h-4" /> You're in with {event.my_stake} BLC in the pot — lineup locked. Results land Friday night.
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          <label className="flex items-center gap-2 border border-yellow-400/40 bg-black/60 px-3 shrink-0">
            <span className="text-[8px] font-black uppercase tracking-[0.2em] text-zinc-500 whitespace-nowrap">My Stake</span>
            <input data-testid="cw-event-stake-input" type="number" min={min} step="1" value={stake} placeholder={String(min)}
              onChange={(e) => setStake(e.target.value)}
              className="w-24 bg-transparent py-3 text-lg font-mono font-bold text-yellow-400 outline-none" />
            <span className="text-[10px] font-black text-zinc-500">BLC</span>
          </label>
          <button data-testid="cw-event-enter-btn" onClick={() => onEnter(amt, lineupToUse)} disabled={busy || !lineupToUse || !(amt >= min)}
            className="flex-1 bg-pb-red text-white font-black uppercase tracking-widest py-3.5 hover:bg-red-500 active:scale-[0.98] transition-all disabled:opacity-40 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" />}
            {!lineupToUse ? "Pick 5 cards below (or save a defense lineup) to register"
              : amt < min ? `Minimum stake is ${min} BLC`
              : `Register — throw ${amt} BLC in the pot${usingSaved ? " (using my defense lineup)" : ""}`}
          </button>
        </div>
      )}
      {event.last_event && (
        <div className="mt-4 pt-3 border-t border-pb-border/60" data-testid="cw-event-last-winner">
          <div className="text-[8px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-1.5">Last Battle — {event.last_event.week_key}</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-yellow-400" />
              <b className="text-yellow-400">{event.last_event.winner_name}</b>
              <span className="text-zinc-400">took {event.last_event.pot_paid} BLC from {event.last_event.entrants} players</span>
            </span>
            {event.last_event.top3?.slice(1).map((r) => (
              <span key={r.placement} className="text-zinc-500 font-mono">#{r.placement} {r.name} · {r.score}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ARENA_IMG = { w: 1264, h: 848 };
const ARENA_SPOTS = {
  left: { cx: 0.389, top: 0.235, h: 0.325, rot: -6, glow: "163,230,53" },
  right: { cx: 0.623, top: 0.230, h: 0.335, rot: 6, glow: "239,68,68" },
};

function ArenaOverlayCard({ side, url }) {
  const [box, setBox] = useState(null);
  useEffect(() => {
    const calc = () => {
      const vw = window.innerWidth, vh = window.innerHeight;
      const s = Math.max(vw / ARENA_IMG.w, vh / ARENA_IMG.h);
      const rw = ARENA_IMG.w * s, rh = ARENA_IMG.h * s;
      const ox = (vw - rw) / 2, oy = (vh - rh) / 2;
      const c = ARENA_SPOTS[side];
      setBox({ left: ox + c.cx * rw, top: oy + c.top * rh, height: c.h * rh });
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [side]);
  if (!box) return null;
  const c = ARENA_SPOTS[side];
  return (
    <img src={url} alt="" data-testid={`backdrop-overlay-${side}`} className="absolute"
      style={{ left: box.left, top: box.top, height: box.height, width: "auto",
               transform: `translateX(-50%) rotate(${c.rot}deg)`,
               filter: `drop-shadow(0 0 14px rgba(${c.glow},0.9)) drop-shadow(0 0 40px rgba(${c.glow},0.5))` }} />
  );
}

const FMT_META = {
  singles: { img: "/icons/cw-fmt-singles.png", glow: "rgba(56,189,248,.7)", tag: "1v1 · 1 card · best of 3 rallies" },
  doubles: { img: "/icons/cw-fmt-doubles.png", glow: "rgba(192,132,252,.7)", tag: "1v1 teams · 2 cards · 4 rallies" },
  mlp: { img: "/icons/cw-fmt-mlp.png", glow: "rgba(248,113,113,.7)", tag: "4-way · 2♂ + 2♀ cards · dreambreaker" },
  classic: { img: "/icons/cw-fmt-classic.png", glow: "rgba(250,204,21,.7)", tag: "5-way war · 5 cards · clutch R5" },
};

const COMBO_LABELS = {
  classic: "Classic", singles_mens: "Men's Singles", singles_womens: "Women's Singles",
  doubles_mens: "Men's Doubles", doubles_womens: "Women's Doubles", doubles_mixed: "Mixed Doubles", mlp: "MLP",
};

export default function CardWars() {
  const [deck, setDeck] = useState(null);
  const [lineup, setLineup] = useState([]);
  const [deckSort, setDeckSort] = useState("newest");
  const [mode, setMode] = useState("practice");
  const [fmt, setFmt] = useState("classic");
  const [dblType, setDblType] = useState("mixed");
  const [snglType, setSnglType] = useState("mens");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(() => { try { return JSON.parse(sessionStorage.getItem("cw_result") || "null"); } catch { return null; } });
  const [lb, setLb] = useState([]);
  const [opponents, setOpponents] = useState([]);
  const [oppSel, setOppSel] = useState([]);
  const [oppQ, setOppQ] = useState("");
  const [oppOpen, setOppOpen] = useState(false);
  const oppTimer = useRef(null);
  const [manual, setManual] = useState(null);
  const [blackLabels, setBlackLabels] = useState([]);
  const [history, setHistory] = useState([]);
  const [backdrop, setBackdrop] = useState(null);
  const [event, setEvent] = useState(null);
  const [eventBusy, setEventBusy] = useState(false);
  const [showArena, setShowArena] = useState(false);
  const [showSkybox, setShowSkybox] = useState(false);
  const { user } = useAuth();
  const load = useCallback(() => {
    api.get("/cardwars/deck").then(({ data }) => {
      setDeck(data);
      setLineup((l) => l.length ? l : (data.my_lineup || []).filter((id) => data.cards.some((c) => c.id === id)));
    }).catch(() => setDeck({ cards: [] }));
    api.get("/cardwars/leaderboard").then(({ data }) => setLb(data)).catch(() => {});
    api.get("/cardwars/opponents").then(({ data }) => setOpponents(data)).catch(() => {});
    api.get("/cardwars/black-label").then(({ data }) => setBlackLabels(data)).catch(() => {});
    api.get("/cardwars/battles").then(({ data }) => setHistory(data)).catch(() => {});
    api.get("/cardwars/backdrop").then(({ data }) => setBackdrop(data)).catch(() => {});
    api.get("/cardwars/event").then(({ data }) => setEvent(data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const location = useLocation();
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("skybox")) {
      setShowSkybox(true);
      p.delete("skybox");
      window.history.replaceState({}, "", window.location.pathname + (p.toString() ? `?${p}` : "") + window.location.hash);
    }
    const bid = p.get("battle") || p.get("replay");
    if (!bid) return;
    api.get(`/cardwars/battles/${bid}`)
      .then(({ data }) => setResult(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    p.delete("battle"); p.delete("replay");
    window.history.replaceState({}, "", window.location.pathname + (p.toString() ? `?${p}` : "") + window.location.hash);
  }, [location.search]);

  const genderNeed = (k = fmt, st = snglType, dt = dblType) =>
    k === "singles" ? (st === "mens" ? "male" : "female")
      : k === "doubles" ? (dt === "mens" ? "male" : dt === "womens" ? "female" : "mixed")
        : null;
  const pickBlockReason = (c) => {
    if (fmt === "mlp") {
      if (!c.gender) return `"${c.name}" has no gender tag yet — it can't play gendered matches`;
      const same = lineup.map((id) => byId[id]).filter((x) => x && x.gender === c.gender).length;
      if (same >= 2) return `MLP needs 2 ♂ + 2 ♀ — you already picked two ${c.gender} cards`;
      return null;
    }
    const need = genderNeed();
    if (!need) return null;
    if (!c.gender) return `"${c.name}" has no gender tag yet — it can't play gendered matches`;
    if (need === "male" && c.gender !== "male") return "This match type needs ♂ MALE cards";
    if (need === "female" && c.gender !== "female") return "This match type needs ♀ FEMALE cards";
    if (need === "mixed") {
      const same = lineup.map((id) => byId[id]).filter((x) => x && x.gender === c.gender).length;
      if (same >= 1) return `Mixed needs one ♂ + one ♀ — you already picked a ${c.gender} card`;
    }
    return null;
  };
  const toggle = (id) => {
    if (lineup.includes(id)) return setLineup((l) => l.filter((x) => x !== id));
    const reason = pickBlockReason(byId[id] || {});
    if (reason) return toast.info(reason);
    if (lineup.length >= maxCards) return;
    sndSlotTick();
    setLineup((l) => (l.length >= maxCards ? l : [...l, id]));
  };
  const setClutch = (id) => setLineup((l) => [...l.filter((x) => x !== id), id]);
  const revalidateLineup = (k, st, dt) => {
    const need = genderNeed(k, st, dt);
    const n = deck?.formats?.[k]?.cards ?? 5;
    setLineup((l) => {
      let cards = l.map((id) => byId[id]).filter(Boolean);
      if (need === "male" || need === "female") cards = cards.filter((c) => c.gender === need);
      else if (need === "mixed") {
        const m = cards.find((c) => c.gender === "male");
        const f = cards.find((c) => c.gender === "female");
        cards = [m, f].filter(Boolean);
      } else if (k === "mlp") {
        const m = cards.filter((c) => c.gender === "male").slice(0, 2);
        const f = cards.filter((c) => c.gender === "female").slice(0, 2);
        cards = [...m, ...f];
      }
      return cards.slice(0, n).map((c) => c.id);
    });
  };
  const pickFmt = (k) => {
    setFmt(k);
    revalidateLineup(k, snglType, dblType);
    setOppSel([]); setOpponents([]);
  };
  const pickSnglType = (st) => { setSnglType(st); revalidateLineup(fmt, st, dblType); setOppSel([]); setOpponents([]); };
  const pickDblType = (dt) => { setDblType(dt); revalidateLineup(fmt, snglType, dt); setOppSel([]); setOpponents([]); };
  const saveDefense = async () => {
    try {
      const { data } = await api.post("/cardwars/defense",
        { copy_ids: lineup, format: fmt, doubles_type: fmt === "doubles" ? dblType : null,
          singles_type: fmt === "singles" ? snglType : null });
      setDeck((d) => ({ ...d, my_defenses: { ...(d.my_defenses || {}), [data.key]: lineup } }));
      toast.success(`${data.label} defense saved — rivals can now find and challenge you in ${data.label}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const searchOpp = (v) => {
    setOppQ(v);
    setOppOpen(true);
    clearTimeout(oppTimer.current);
    oppTimer.current = setTimeout(() => {
      const p = new URLSearchParams({ format: fmt });
      if (v.trim()) p.set("q", v.trim());
      if (fmt === "doubles") p.set("doubles_type", dblType);
      if (fmt === "singles") p.set("singles_type", snglType);
      api.get(`/cardwars/opponents?${p.toString()}`)
        .then(({ data }) => setOpponents(data)).catch(() => {});
    }, 300);
  };
  const pickOpp = (o) => {
    setOppSel((s) => s.some((x) => x.id === o.id) ? s
      : s.length >= maxRivals ? (toast.info(`Max ${maxRivals} rival${maxRivals === 1 ? "" : "s"} in ${fmtInfo.label} — remove one first`), s) : [...s, o]);
    setOppQ("");
    setOppOpen(false);
  };
  const rewatch = (id) =>
    api.get(`/cardwars/battles/${id}`).then(({ data }) => setResult(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  const battle = async () => {
    setBusy(true);
    sndBattleEntrance();
    try {
      const { data } = await api.post("/cardwars/battle",
        { copy_ids: lineup, mode, format: fmt, doubles_type: fmt === "doubles" ? dblType : null,
          singles_type: fmt === "singles" ? snglType : null, opponent_ids: oppSel.map((o) => o.id) });
      setResult(data);
      try { sessionStorage.setItem("cw_result", JSON.stringify(data)); } catch { /* storage full */ }
      if (data.daily_bonus > 0) toast.success(`+${data.daily_bonus} BLC daily battle bonus — day ${data.streak} streak! 🔥`, { duration: 6000 });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const enterEvent = async (amount, lineupToUse) => {
    setEventBusy(true);
    try {
      const { data } = await api.post("/cardwars/event/enter", { copy_ids: lineupToUse, amount });
      setEvent((ev) => ({ ...ev, ...data }));
      toast.success(`You're in with ${amount} BLC in the pot — good luck Friday night! 🔥`, { duration: 6000 });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEventBusy(false); }
  };

  if (!deck) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-yellow-400 animate-spin" /></div>;
  const byId = Object.fromEntries(deck.cards.map((c) => [c.id, c]));
  const RARITY_RANK = { exclusive: 0, icon: 1, legendary: 2, elite: 3, gold: 4, holo: 5, gem: 6, rare: 7, common: 8, standard: 9 };
  const sortedDeckCards = deckSort === "newest" ? deck.cards
    : [...deck.cards].sort((a, b) => deckSort === "power"
        ? (b.stats?.overall || 0) - (a.stats?.overall || 0)
        : (RARITY_RANK[a.rarity] ?? 9) - (RARITY_RANK[b.rarity] ?? 9) || (b.stats?.overall || 0) - (a.stats?.overall || 0));
  const fmtInfo = deck.formats?.[fmt] || { label: "Bullpen Classic", cards: 5, seats: 5, fee: deck.entry_blc, pot: deck.pot, win_payout: deck.win_payout };
  const maxCards = fmtInfo.cards;
  const maxRivals = fmtInfo.seats - 1;
  const defKey = fmt === "singles" ? `singles_${snglType}` : fmt === "doubles" ? `doubles_${dblType}` : fmt;
  const comboLbl = COMBO_LABELS[defKey] || fmtInfo.label;
  const hasDef = ((deck.my_defenses || {})[defKey] || []).length === maxCards;

  return (
    <div data-testid="cardwars-page" className="relative">
      <style>{CW_CSS}</style>
      <div className="fixed inset-0 z-0 pointer-events-none" data-testid="cardwars-battle-bg">
        <img src={backdrop?.mode === "ai" && backdrop?.image_url ? `${process.env.REACT_APP_BACKEND_URL}${backdrop.image_url}` : "/sdb/battleground.jpg"} alt="" className="w-full h-full object-cover" />
        {backdrop?.mode === "overlay" && backdrop?.left?.url && (
          <ArenaOverlayCard side="left" url={`${process.env.REACT_APP_BACKEND_URL}${backdrop.left.url}`} />
        )}
        {backdrop?.mode === "overlay" && backdrop?.right?.url && (
          <ArenaOverlayCard side="right" url={`${process.env.REACT_APP_BACKEND_URL}${backdrop.right.url}`} />
        )}
        <div className="absolute inset-0 bg-black/72" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/85" />
      </div>
      <div className="relative z-[1]">
      <div className="flex items-center gap-2 mb-3 animate-fade-up flex-wrap">
        <span className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.3em] text-yellow-400 border border-yellow-400/60 bg-yellow-400/10 px-2.5 py-1">
          <Sparkles className="w-3 h-3" /> Early Access
        </span>
        {deck.streak > 0 && (
          <span className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.3em] text-orange-400 border border-orange-400/50 bg-orange-400/10 px-2.5 py-1" data-testid="cw-streak-chip">
            <Flame className="w-3 h-3" /> Day {deck.streak} streak
          </span>
        )}
        {!deck.daily_done && (
          <span className="text-[9px] uppercase tracking-widest text-zinc-500" data-testid="cw-daily-hint">
            first battle today pays +{deck.daily_bonus_blc} BLC
          </span>
        )}
        <span className="flex gap-2 ml-auto">
          <button data-testid="cw-manual-btn" onClick={() => setManual("top")}
            className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.2em] text-zinc-300 border border-zinc-600 px-3 py-1.5 hover:border-yellow-400 hover:text-yellow-400 transition-colors">
            <BookOpen className="w-3 h-3" /> How to Play
          </button>
          <button data-testid="cw-faq-btn" onClick={() => setManual("faq")}
            className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.2em] text-zinc-300 border border-zinc-600 px-3 py-1.5 hover:border-yellow-400 hover:text-yellow-400 transition-colors">
            <HelpCircle className="w-3 h-3" /> FAQ
          </button>
        </span>
      </div>
      <PageHeader kicker="Collect · Battle · Discover" title="Bullpen: Card Wars"
        sub="Court Clash is LIVE. Pick your format — Singles, Doubles, MLP Style or the full 5-card Bullpen Classic — build your lineup and battle rivals or Bullpen Bots for the pot." />

      {/* The Skybox — spectator suite */}
      <button data-testid="cw-skybox-btn" onClick={() => setShowSkybox(true)}
        className="cw-btn-skybox w-full sm:w-[440px] h-[64px] sm:h-[72px] mb-6 active:scale-[0.985] transition-transform animate-fade-up">
        <span className="cw-btn-inner text-[11px] sm:text-xs font-black uppercase tracking-[0.25em] flex-col gap-0.5">
          <span>🏟 Enter the Skybox</span>
          <span className="text-[7px] sm:text-[8px] tracking-[0.3em] opacity-80">Watch the week's battles · call the winners</span>
        </span>
      </button>

      {/* Fill Your Vault — pack purchases */}
      <CardWarsPacks />

      {/* Court Clash */}
      <div className="border border-yellow-400/40 bg-pb-surface p-5 mb-8 animate-fade-up" data-testid="cw-clash-panel">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="font-display font-black uppercase text-lg flex items-center gap-2"><Swords className="w-5 h-5 text-yellow-400" /> Court Clash</span>
          {deck.psi_boost > 0 && (
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#A3E635] border border-[#A3E635]/50 bg-[#A3E635]/10 px-2.5 py-1" data-testid="cw-psi-boost" title="Your real-world PSI boosts every card you field">
              PSI Boost +{deck.psi_boost} on every card
            </span>
          )}
          <div className="flex gap-2 ml-auto">
            <button data-testid="cw-mode-practice" onClick={() => setMode("practice")}
              className={`text-[10px] font-black uppercase tracking-widest px-4 py-2 border transition-colors ${mode === "practice" ? "bg-[#A3E635] text-black border-[#A3E635]" : "border-pb-border text-zinc-400 hover:border-[#A3E635]"}`}>
              Practice · Free
            </button>
            <button data-testid="cw-mode-ranked" onClick={() => setMode("ranked")}
              className={`text-[10px] font-black uppercase tracking-widest px-4 py-2 border transition-colors ${mode === "ranked" ? "bg-yellow-400 text-black border-yellow-400" : "border-pb-border text-zinc-400 hover:border-yellow-400"}`}>
              Ranked · {fmtInfo.fee} BLC
            </button>
          </div>
        </div>
        {/* Match Format selector */}
        <div className="mb-4" data-testid="cw-format-row">
          <div className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-500 mb-2">Match Format</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
            {Object.entries(deck.formats || {}).map(([k, f]) => (
              <button key={k} data-testid={`cw-fmt-${k}`} onClick={() => pickFmt(k)}
                className={`cw-fmt-plate active:scale-[0.97] ${fmt === k ? "sel" : ""}`}
                style={{ "--fmt-glow": FMT_META[k]?.glow }}>
                <span className="cw-fmt-bg" style={{ backgroundImage: `url('${FMT_META[k]?.img}')` }} />
                <span className="cw-fmt-inner">
                  <span className="text-[11px] font-black uppercase tracking-[0.2em] text-white"
                    style={fmt === k ? { textShadow: `0 0 3px #000, 0 0 6px #000, 0 0 12px ${FMT_META[k]?.glow}, 0 0 24px ${FMT_META[k]?.glow}` }
                      : { textShadow: "0 0 3px #000, 0 0 6px #000, 0 0 12px rgba(0,0,0,.95), 0 2px 3px #000" }}>
                    {f.label}
                  </span>
                  <span className={`text-[7px] uppercase tracking-[0.18em] ${fmt === k ? "text-zinc-300" : "text-zinc-200"}`}
                    style={{ textShadow: "0 0 3px #000, 0 0 6px #000, 0 1px 2px #000" }}>{FMT_META[k]?.tag}</span>
                </span>
              </button>
            ))}
          </div>
          {fmt === "singles" && (
            <div className="flex flex-wrap items-center gap-2 mt-2.5" data-testid="cw-singles-type-row">
              <span className="text-[9px] font-black uppercase tracking-[0.25em] text-sky-300/80">Singles Type:</span>
              {[["mens", "Men's", "One ♂ card"], ["womens", "Women's", "One ♀ card"]].map(([k, lbl, hint]) => (
                <button key={k} data-testid={`cw-sgl-${k}`} onClick={() => pickSnglType(k)} title={hint}
                  className={`cw-dbl-chip active:scale-95 ${snglType === k ? "sel" : ""}`}>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-purple-200">{lbl}
                    <span className="ml-1.5 text-[8px] text-purple-400/80">{hint}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {fmt === "doubles" && (
            <div className="flex flex-wrap items-center gap-2 mt-2.5" data-testid="cw-doubles-type-row">
              <span className="text-[9px] font-black uppercase tracking-[0.25em] text-purple-300/80">Doubles Type:</span>
              {[["mens", "Men's", "Two ♂ cards"], ["womens", "Women's", "Two ♀ cards"], ["mixed", "Mixed", "One ♂ + one ♀"]].map(([k, lbl, hint]) => (
                <button key={k} data-testid={`cw-dbl-${k}`} onClick={() => pickDblType(k)} title={hint}
                  className={`cw-dbl-chip active:scale-95 ${dblType === k ? "sel" : ""}`}>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-purple-200">{lbl}
                    <span className="ml-1.5 text-[8px] text-purple-400/80">{hint}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid="cw-defense-status">
            <span className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1">
              <Shield className="w-3 h-3" /> My Defenses:
            </span>
            {Object.entries(COMBO_LABELS).map(([k, lbl]) => {
              const saved = ((deck.my_defenses || {})[k] || []).length > 0;
              return (
                <span key={k} data-testid={`cw-def-chip-${k}`}
                  title={saved ? `${lbl} defense saved — rivals can find and challenge you in ${lbl}` : `No ${lbl} defense yet — you won't appear in ${lbl} rival searches`}
                  className={`text-[8px] font-black uppercase tracking-widest px-2 py-1 border ${saved ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10" : "border-zinc-700 text-zinc-600"}`}>
                  {saved ? "✓ " : ""}{lbl}
                </span>
              );
            })}
          </div>
        </div>
        {mode === "ranked" && (
          <div className="flex items-center gap-2 text-xs text-zinc-400 mb-4" data-testid="cw-ranked-note">
            <Coins className="w-3.5 h-3.5 text-yellow-400" />
            {fmtInfo.label} entry {fmtInfo.fee} BLC · {fmtInfo.pot} BLC pot · win it and take <b className="text-yellow-400 font-mono">{fmtInfo.win_payout} BLC</b> (house keeps {deck.house_cut_pct}%)
          </div>
        )}
        {/* Opponent picker — searchable rivals, bots fill the rest */}
        <div className="mb-4" data-testid="cw-opponent-row">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-500">Rivals (pick up to {maxRivals}):</span>
            <button data-testid="cw-opp-bots" onClick={() => setOppSel([])}
              className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 border transition-colors ${oppSel.length === 0 ? "bg-pb-elevated border-zinc-400 text-white" : "border-pb-border text-zinc-500 hover:border-zinc-400"}`}>
              All Bots
            </button>
            {oppSel.map((o) => {
              const [, cls] = pwrTier(o.power);
              return (
                <span key={o.id} data-testid={`cw-opp-sel-${o.id}`}
                  className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest pl-3 pr-1.5 py-1.5 border bg-pb-red text-black border-pb-red">
                  <Swords className="w-2.5 h-2.5" /> {o.name}
                  {o.h2h && <span className="text-[8px] font-mono font-black px-1 py-0.5 bg-black text-white" title={`Your head-to-head record vs ${o.name}`}>{o.h2h.wins}-{o.h2h.losses}</span>}
                  <span className={`text-[8px] font-mono font-black px-1 py-0.5 border ${cls} bg-black`}>{o.power}</span>
                  <button data-testid={`cw-opp-remove-${o.id}`} onClick={() => setOppSel((s) => s.filter((x) => x.id !== o.id))}
                    className="hover:text-white"><X className="w-3 h-3" /></button>
                </span>
              );
            })}
            {oppSel.length < maxRivals && (
              <div className="relative">
                <input data-testid="cw-opp-search" value={oppQ}
                  onChange={(e) => searchOpp(e.target.value)} onFocus={() => { setOppOpen(true); if (!opponents.length) searchOpp(""); }}
                  onBlur={() => setTimeout(() => setOppOpen(false), 200)}
                  placeholder="Search rivals…"
                  className="w-44 bg-black/50 border border-pb-border px-3 py-1.5 text-xs text-white focus:outline-none focus:border-pb-red/70 transition-colors" />
                {oppOpen && opponents.length > 0 && (
                  <div className="absolute z-30 top-full left-0 mt-1 w-72 max-h-64 overflow-y-auto border border-pb-border bg-pb-surface shadow-[0_10px_30px_rgba(0,0,0,.6)]" data-testid="cw-opp-dropdown">
                    {opponents.filter((o) => !oppSel.some((x) => x.id === o.id)).map((o) => {
                      const [tier, cls] = pwrTier(o.power);
                      return (
                        <button key={o.id} data-testid={`cw-opp-${o.id}`} onMouseDown={(e) => { e.preventDefault(); pickOpp(o); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-pb-elevated transition-colors">
                          <Swords className="w-3 h-3 text-pb-red shrink-0" />
                          <span className="text-xs font-bold flex-1 truncate">{o.name}</span>
                          {o.h2h && (
                            <span data-testid={`cw-opp-h2h-${o.id}`} title={`Your head-to-head record vs ${o.name}`}
                              className={`text-[8px] font-mono font-black px-1.5 py-0.5 border shrink-0 ${o.h2h.wins > o.h2h.losses ? "border-[#A3E635]/60 text-[#A3E635]" : o.h2h.wins < o.h2h.losses ? "border-pb-red/60 text-pb-red" : "border-zinc-500 text-zinc-400"}`}>
                              {o.h2h.wins}-{o.h2h.losses} vs you
                            </span>
                          )}
                          <span className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 border ${cls}`} data-testid={`cw-opp-power-${o.id}`}>
                            {tier} · {o.power}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <span className="text-[9px] text-zinc-600 uppercase tracking-widest" data-testid="cw-opp-fill-note">
              {`${oppSel.length} rival${oppSel.length === 1 ? "" : "s"} + ${maxRivals - oppSel.length} bot${maxRivals - oppSel.length === 1 ? "" : "s"}`}
            </span>
          </div>
          {oppSel.length > 0 && (
            <div className="mt-1.5 text-[9px] uppercase tracking-widest text-zinc-500" data-testid="cw-opp-power-note">
              Defense power = their saved {comboLbl} defense combined — you never see the cards, just how strong the wall is.
            </div>
          )}
        </div>
        {/* Lineup slots */}
        <div className={`grid gap-3 mb-4 ${maxCards === 1 ? "grid-cols-3 sm:grid-cols-5" : maxCards === 2 ? "grid-cols-2 sm:grid-cols-5" : maxCards === 4 ? "grid-cols-4 sm:grid-cols-5" : "grid-cols-5"}`} data-testid="cw-lineup">
          {[...Array(maxCards)].map((_, i) => {
            const c = byId[lineup[i]];
            const isClutchSlot = fmt === "classic" && i === 4;
            const slotLbl = fmt === "singles" ? "Your Ace" : fmt === "doubles" ? (i === 0 ? "Partner 1" : "Partner 2") : `Card ${i + 1}`;
            return (
              <div key={i} data-testid={`cw-lineup-slot-${i + 1}`} className="cw-slot">
                <div className="cw-slot-card">
                  {c ? (
                    <span key={c.id} className="cw-snap">
                      <img src={c.url} alt="" className={`cw-card-img${isClutchSlot ? " clutch" : ""}`} style={{ animationDelay: `${i * 0.45}s` }} />
                      <span className="cw-snap-flash" />
                    </span>
                  ) : (
                    <span className="cw-slot-empty">＋</span>
                  )}
                  <span className={`cw-slot-pad${c ? (isClutchSlot ? " clutch" : " gold") : ""}`} />
                </div>
                {isClutchSlot && c && (
                  <span className="cw-clutch-tag" data-testid="cw-clutch-slot-badge"
                    title={`${c.name} plays Round 5 — the CLUTCH bonus round`}>⚡ CLUTCH</span>
                )}
                <span className="cw-slot-lbl">{slotLbl}</span>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button data-testid="cw-battle-btn" onClick={battle} disabled={busy || lineup.length !== maxCards}
            className="cw-btn-battle flex-1 h-[72px] sm:h-[84px] active:scale-[0.985] transition-transform disabled:opacity-40">
            <span className="cw-btn-inner text-xs sm:text-sm font-black uppercase tracking-[0.22em] gap-2.5">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Swords className="w-4 h-4" />}
              {lineup.length !== maxCards ? `Pick ${maxCards - lineup.length} more card${maxCards - lineup.length === 1 ? "" : "s"}` : mode === "ranked" ? `Enter the Court — ${fmtInfo.fee} BLC` : "Enter the Court — Free"}
            </span>
          </button>
          <button data-testid="cw-save-lineup-btn" onClick={saveDefense} disabled={lineup.length !== maxCards}
            title={`Save this lineup as your ${comboLbl} defense — rivals can only find and battle you in ${comboLbl} once it's saved`}
            className="cw-btn-defense shrink-0 h-[72px] sm:h-[84px] w-full sm:w-[300px] active:scale-[0.985] transition-transform disabled:opacity-40">
            <span className="cw-btn-inner text-[10px] font-black uppercase tracking-[0.14em] gap-2">
              <Shield className="w-3.5 h-3.5" /> {hasDef ? "Update" : "Save"} {comboLbl} Defense
            </span>
          </button>
        </div>
        {!hasDef && (
          <div className="mt-2 text-[9px] uppercase tracking-widest text-cyan-300/70" data-testid="cw-no-defense-hint">
            No {comboLbl} defense saved yet — rivals can't find or battle you in {comboLbl} until you save one.
          </div>
        )}

        {/* Deck */}
        <div className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-500">Your Battle Deck ({deck.cards.length}) — tap cards in the order they'll play</div>
            <div className="flex items-center gap-1" data-testid="cw-deck-sort">
              <span className="text-[8px] uppercase tracking-[0.25em] text-zinc-600 mr-1">Sort</span>
              {[["newest", "Newest"], ["power", "Power"], ["rarity", "Rarity"]].map(([k, l]) => (
                <button key={k} data-testid={`cw-deck-sort-${k}`} onClick={() => setDeckSort(k)}
                  className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 border transition-colors ${deckSort === k ? "border-yellow-400 text-yellow-300 bg-yellow-400/10" : "border-pb-border text-zinc-500 hover:border-zinc-500"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {deck.cards.length === 0 ? (
            <div className="text-sm text-zinc-500 border border-pb-border p-6 text-center" data-testid="cw-empty-deck">
              Your vault is empty — rip a pack in the Card Vault to build your first battle deck.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="cw-deck-grid">
              {sortedDeckCards.map((c) => (
                <DeckCard key={c.id} c={c} order={lineup.indexOf(c.id) + 1 || null}
                  dimmed={!lineup.includes(c.id) && !!pickBlockReason(c)}
                  isClutch={fmt === "classic" && lineup.length === 5 && lineup[4] === c.id}
                  onSetClutch={fmt === "classic" && lineup.length === 5 ? () => setClutch(c.id) : null}
                  onClick={() => toggle(c.id)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Friday Night Bullpen Battle */}
      <FridayNightPanel event={event} lineup={lineup} savedLineup={deck?.my_lineup || []} busy={eventBusy} onEnter={enterEvent} onArena={() => setShowArena(true)} />

      {/* Direct Callouts */}
      <Callouts lineup={lineup} savedLineup={deck?.my_lineup || []} />

      {/* Leaderboard */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <div className="bg-pb-surface border border-pb-border p-5 animate-fade-up" data-testid="cw-leaderboard">
          <div className="font-display font-black uppercase flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-yellow-400" /> Clash Leaderboard</div>
          {lb.length === 0 ? (
            <div className="text-xs text-zinc-600">No battles yet — be the first name on the board.</div>
          ) : (
            <div className="space-y-1.5">
              {lb.map((r, i) => (
                <div key={r.user_id} className="flex items-center gap-3 px-3 py-2 bg-pb-elevated/50" data-testid={`cw-lb-row-${i}`}>
                  <span className={`w-5 text-center font-display font-black ${i === 0 ? "text-yellow-400" : "text-zinc-600"}`}>{i + 1}</span>
                  <span className="flex-1 text-sm font-semibold truncate">{r.name}</span>
                  <span className="text-xs font-mono text-zinc-400">{r.wins}W / {r.battles}</span>
                  {r.blc_won > 0 && <span className="text-xs font-mono font-bold text-yellow-400">+{r.blc_won} BLC</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="relative animate-fade-up">
          <HuntsSection />
        </div>
      </div>

      {/* Recent Clashes */}
      {history.length > 0 && (
        <div className="bg-pb-surface border border-pb-border p-5 mb-8 animate-fade-up" data-testid="cw-recent-clashes">
          <div className="font-display font-black uppercase flex items-center gap-2 mb-1"><Swords className="w-4 h-4 text-yellow-400" /> Recent Clashes</div>
          <p className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Your last {history.length} battles — rewatch any of them round by round</p>
          <div className="space-y-1.5">
            {history.map((b) => (
              <div key={b.id} className="flex items-center gap-3 px-3 py-2 bg-pb-elevated/50 flex-wrap" data-testid={`cw-history-row-${b.id}`}>
                <span className={`w-8 text-center text-xs font-display font-black shrink-0 ${b.placement === 1 ? "text-yellow-400" : "text-zinc-500"}`}>#{b.placement}</span>
                <span className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 border shrink-0 ${b.mode === "ranked" ? "border-pb-red/60 text-pb-red" : "border-zinc-600 text-zinc-400"}`}>{b.mode}</span>
                {b.format_label && b.format !== "classic" && (
                  <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 border shrink-0 border-sky-500/50 text-sky-300" data-testid={`cw-history-fmt-${b.id}`}>{b.format_label}</span>
                )}
                <span className="flex-1 min-w-0 text-xs text-zinc-300 truncate">
                  {b.is_challenger
                    ? <>You challenged <b className="text-white">{b.rivals.length ? b.rivals.join(", ") : "the Bullpen bots"}</b></>
                    : <><b className="text-white">{b.challenger_name}</b> challenged your defense</>}
                </span>
                {b.payout > 0 && <span className="text-xs font-mono font-bold text-yellow-400 shrink-0">+{b.payout} BLC</span>}
                <span className="text-[9px] font-mono text-zinc-600 shrink-0 hidden sm:inline">{b.created_at ? new Date(b.created_at).toLocaleDateString() : ""}</span>
                <button data-testid={`cw-rewatch-${b.id}`} onClick={() => rewatch(b.id)}
                  className="shrink-0 text-[9px] font-black uppercase tracking-widest border border-yellow-400/60 text-yellow-400 px-3 py-1.5 hover:bg-yellow-400 hover:text-black transition-colors">
                  ▶ Rewatch
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Black Label Registry */}
      <div className="relative border border-yellow-300/50 bg-gradient-to-b from-yellow-950/25 to-black p-5 mb-8 animate-fade-up overflow-hidden" data-testid="black-label-registry">
        <div className="flex items-center gap-2 mb-1">
          <Crown className="w-5 h-5 text-yellow-300" />
          <span className="font-display font-black uppercase text-lg text-yellow-300">★ Black Label Registry</span>
        </div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">The hall of fame — every card to achieve a perfect signed Black Label 10</p>
        {blackLabels.length === 0 ? (
          <div className="text-sm text-zinc-500 border border-dashed border-yellow-300/30 p-6 text-center" data-testid="black-label-empty">
            No card has achieved BLACK LABEL yet. A perfect Bullpen Black Label 10 slab, signed by the player — the throne awaits.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {blackLabels.map((c) => (
              <div key={c.id} className="border border-yellow-300/70 bg-black p-1.5 shadow-[0_0_16px_rgba(253,224,71,.3)]" data-testid={`black-label-${c.id}`}>
                <img src={c.url} alt={c.name} loading="lazy" className="w-full aspect-[3/4] object-contain bg-black cursor-zoom-in"
                  onClick={() => zoomCard(c.url, c.name, null, c)} />
                <div className="mt-1 text-[9px] font-black uppercase text-yellow-300 truncate">★ {c.name}</div>
                <div className="text-[7.5px] font-mono text-zinc-500 truncate">{c.cert} · held by {c.owner_name}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      </div>
      {result && <BattleResult result={result} onClose={() => { sessionStorage.removeItem("cw_result"); setResult(null); load(); }} />}
      {showArena && <BullpenArena meId={user?.id} onClose={() => setShowArena(false)} />}
      {showSkybox && <Skybox meId={user?.id} onClose={() => setShowSkybox(false)} />}
      {manual && <CardWarsManual startAtFaq={manual === "faq"} onClose={() => setManual(null)} />}
    </div>
  );
}
